/** Workshop extension rules; these IDs never replace original engine enums. */
export const SCHEMA_VERSION = 1;
export const RULESETS = { izombie: 'izombie-v1', defense: 'defense-v1' };
export const MODES = ['izombie', 'defense'];
export const SORTS = ['recommended', 'newest', 'popular'];
export const TAGS = ['beginner', 'economy', 'strategy'];
export const IZOMBIE_PLANTS = [
    'sunflower', 'peashooter', 'snow-pea', 'repeater', 'wall-nut', 'tall-nut',
    'potato-mine', 'chomper', 'squash', 'spikeweed', 'torchwood', 'threepeater',
];
export const DEFENSE_PLANTS = [...IZOMBIE_PLANTS, 'cherry-bomb'];
export const ZOMBIES = ['normal', 'conehead', 'pole-vaulter', 'buckethead', 'screen-door', 'football'];
export const LIMITS = {
    jsonBytes: 64 * 1024, compressedBytes: 24 * 1024, codeCharacters: 33000,
    importLinkCharacters: 2000, plants: 30, spawns: 300, simultaneousSpawns: 20,
    liveZombies: 100, lastSpawnTick: 120000,
};
export const GAME_LANGUAGES = { 'zh-CN': true, en: false, ja: false };
export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function isMode(value) { return MODES.some(x => x === value); }
export function isSort(value) { return SORTS.some(x => x === value); }
export class LevelValidationError extends Error {
    issues;
    constructor(issues) {
        super(issues.map(x => x.path + ': ' + x.message).join('; '));
        this.issues = issues;
        this.name = 'LevelValidationError';
    }
}
function fail(path, message) {
    throw new LevelValidationError([{ path, message }]);
}
function object(value, keys, path) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        fail(path, '需要对象');
    const result = value;
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null)
        fail(path, '需要普通 JSON 对象');
    for (const key of Object.keys(result))
        if (!keys.includes(key))
            fail(path + '.' + key, '未知字段');
    for (const key of keys)
        if (!Object.hasOwn(result, key))
            fail(path + '.' + key, '缺少字段');
    return result;
}
function exact(value, expected, path) {
    if (value !== expected)
        fail(path, '必须为 ' + String(expected));
}
function integer(value, min, max, step, path) {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < min || value > max || value % step !== 0) {
        fail(path, '需要 ' + min + '～' + max + ' 的整数，步长 ' + step);
    }
    return value;
}
function text(value, min, max, path) {
    if (typeof value !== 'string')
        fail(path, '需要文本');
    const size = [...value].length;
    if (size < min || size > max || (min > 0 && value.trim().length === 0))
        fail(path, '文本码点数越界');
}
function list(value, min, max, path) {
    if (!Array.isArray(value) || value.length < min || value.length > max)
        fail(path, '数组长度越界');
    return value;
}
function choice(value, choices, path) {
    if (typeof value !== 'string' || !choices.includes(value))
        fail(path, '不支持的类型');
}
function cards(value, choices, max, path) {
    const items = list(value, 1, max, path);
    items.forEach((x, i) => choice(x, choices, path + '[' + i + ']'));
    if (new Set(items).size !== items.length)
        fail(path, '不允许重复');
}
/** UTF-8 length without DOM, Node or Worker dependencies. */
export function utf8ByteLength(value) {
    let size = 0;
    for (const c of value) {
        const cp = c.codePointAt(0);
        size += cp <= 0x7f ? 1 : cp <= 0x7ff ? 2 : cp <= 0xffff ? 3 : 4;
    }
    return size;
}
export function validateLevel(input) {
    let json;
    try {
        json = JSON.stringify(input);
    }
    catch {
        fail('$', '需要可序列化 JSON');
    }
    if (!json || utf8ByteLength(json) > LIMITS.jsonBytes)
        fail('$', 'JSON 超过 64 KiB 或不是 JSON');
    if (!input || typeof input !== 'object')
        fail('$', '需要关卡对象');
    const mode = input.mode;
    if (!isMode(mode))
        fail('$.mode', '不支持的玩法');
    const common = ['schemaVersion', 'seed', 'meta', 'mode', 'ruleset', 'board', 'initialSun'];
    const v = object(input, [...common, ...(mode === 'izombie' ? ['zombieCards', 'plants'] : ['plantCards', 'skySun', 'lawnMowers', 'spawns'])], '$');
    exact(v.schemaVersion, SCHEMA_VERSION, '$.schemaVersion');
    exact(v.ruleset, RULESETS[mode], '$.ruleset');
    integer(v.seed, 0, 0xffffffff, 1, '$.seed');
    const meta = object(v.meta, ['title', 'description', 'tags'], '$.meta');
    text(meta.title, 1, 40, '$.meta.title');
    text(meta.description, 0, 300, '$.meta.description');
    const tags = list(meta.tags, 0, 3, '$.meta.tags');
    tags.forEach((x, i) => choice(x, TAGS, '$.meta.tags[' + i + ']'));
    if (new Set(tags).size !== tags.length)
        fail('$.meta.tags', '不允许重复标签');
    const board = object(v.board, mode === 'izombie' ? ['rows', 'cols', 'terrain', 'plantColumns'] : ['rows', 'cols', 'terrain'], '$.board');
    exact(board.rows, 5, '$.board.rows');
    exact(board.cols, 9, '$.board.cols');
    exact(board.terrain, mode === 'izombie' ? 'night' : 'day', '$.board.terrain');
    integer(v.initialSun, mode === 'izombie' ? 50 : 0, 2000, 25, '$.initialSun');
    if (mode === 'izombie') {
        const columns = integer(board.plantColumns, 4, 6, 1, '$.board.plantColumns');
        cards(v.zombieCards, ZOMBIES, 6, '$.zombieCards');
        const occupied = new Set();
        list(v.plants, 0, LIMITS.plants, '$.plants').forEach((item, i) => {
            const path = '$.plants[' + i + ']';
            const p = object(item, ['type', 'row', 'col'], path);
            choice(p.type, IZOMBIE_PLANTS, path + '.type');
            integer(p.row, 0, 4, 1, path + '.row');
            integer(p.col, 0, columns - 1, 1, path + '.col');
            const cell = String(p.row) + ':' + p.col;
            if (occupied.has(cell))
                fail(path, '重复格子');
            occupied.add(cell);
        });
    }
    else {
        cards(v.plantCards, DEFENSE_PLANTS, 8, '$.plantCards');
        for (const key of ['skySun', 'lawnMowers'])
            if (typeof v[key] !== 'boolean')
                fail('$.' + key, '需要布尔值');
        const perTick = new Map();
        list(v.spawns, 1, LIMITS.spawns, '$.spawns').forEach((item, i) => {
            const path = '$.spawns[' + i + ']';
            const p = object(item, ['atTick', 'row', 'type'], path);
            const tick = integer(p.atTick, 0, LIMITS.lastSpawnTick, 10, path + '.atTick');
            integer(p.row, 0, 4, 1, path + '.row');
            choice(p.type, ZOMBIES, path + '.type');
            const count = (perTick.get(tick) ?? 0) + 1;
            if (count > LIMITS.simultaneousSpawns)
                fail(path, '同 tick 最多 20 次出生');
            perTick.set(tick, count);
        });
    }
    return JSON.parse(json);
}
function sorted(value) {
    if (Array.isArray(value))
        return value.map(sorted);
    if (value && typeof value === 'object')
        return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([k, v]) => [k, sorted(v)]));
    return value;
}
export function normalizeLevel(input) {
    const level = validateLevel(input);
    if (level.mode === 'izombie')
        level.plants.sort((a, b) => a.row - b.row || a.col - b.col);
    // Stable sort retains meaningful array order among simultaneous births.
    else
        level.spawns.sort((a, b) => a.atTick - b.atTick);
    return sorted(level);
}
export function canonicalLevelJSON(input, gameplayOnly = false) {
    const level = normalizeLevel(input);
    if (gameplayOnly) {
        const { meta: _meta, ...gameplay } = level;
        return JSON.stringify(gameplay);
    }
    return JSON.stringify(level);
}
/** Inject platform SHA-256; package itself imports no platform globals. */
export async function hashLevel(input, sha256) {
    const content = canonicalLevelJSON(input);
    const gameplay = canonicalLevelJSON(input, true);
    return { contentHash: await sha256(content), gameplayHash: await sha256(gameplay) };
}
